/**
 * MusicManager — background music for Woodchuck using HTML5 Audio.
 *
 * Plays the existing MP3 at /assets/1946_268.mp3 on loop.
 * HTML5 Audio handles autoplay restrictions gracefully — play() rejects
 * silently if called outside a gesture, then succeeds on the next
 * gesture-driven call. No AudioContext, no race conditions.
 *
 * SoundManager keeps its own AudioContext for SFX — zero conflict.
 * Mute state persisted to localStorage.
 */

const STORAGE_KEY = "woodchuck_music_muted";
const MUSIC_SRC = "/assets/1946_268.mp3";
const DEFAULT_VOLUME = 0.35;

let audio = null;
let wantPlaying = false;

function ensureAudio() {
  if (audio) return audio;
  audio = new Audio(MUSIC_SRC);
  audio.loop = true;
  audio.volume = isMuted() ? 0 : DEFAULT_VOLUME;
  return audio;
}

function isMuted() {
  return localStorage.getItem(STORAGE_KEY) === "1";
}

function tryPlay() {
  if (!wantPlaying) return;
  const el = ensureAudio();
  el.play().catch(() => {
    // Autoplay blocked — will succeed on next user-gesture call
  });
}

const MusicManager = {
  /**
   * Request music playback. Safe to call from anywhere.
   * If called outside a user gesture, audio won't start until
   * the next gesture-driven call to play().
   */
  play() {
    wantPlaying = true;
    tryPlay();
  },

  /**
   * Stop music playback.
   */
  stop() {
    wantPlaying = false;
    const el = ensureAudio();
    el.pause();
    el.currentTime = 0;
  },

  /**
   * Toggle mute on/off. Returns the new muted state.
   */
  toggle() {
    const muted = !this.isMuted();
    localStorage.setItem(STORAGE_KEY, muted ? "1" : "0");

    const el = ensureAudio();
    el.volume = muted ? 0 : DEFAULT_VOLUME;

    if (!muted && wantPlaying) {
      tryPlay();
    }

    return muted;
  },

  /**
   * Check if music is muted.
   */
  isMuted() {
    return isMuted();
  },

  /**
   * Set master volume (0-1).
   */
  setVolume(v) {
    const clamped = Math.max(0, Math.min(1, v));
    const el = ensureAudio();
    el.volume = this.isMuted() ? 0 : clamped;
  },

  /**
   * No-op — single MP3, no layered audio.
   * Kept for API compatibility (combo tier music layers, not used yet).
   */
  setComboTier() {},
};

export default MusicManager;
